import PropTypes from 'prop-types';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import CountingChart from './CountingChart';
import LoadingSkeleton from './LoadingSkeleton';

const StatCard = ({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  sparklineData = [],
  icon,
  accentColor,
  loading = false,
}) => {
  const hasSparkline = sparklineData.length > 0;
  const positive = trend == null || trend >= 0;
  const trendColor = positive ? 'success' : 'error';

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        ...(accentColor && {
          borderTop: `3px solid ${accentColor}`,
        }),
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        {/* Header row: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          {icon && (
            <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 16 }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {title}
          </Typography>
        </Box>

        {/* Value with Smooth 0.8s Crossfade Loading Animation */}
        <Box sx={{ py: '2px' }}>
          <LoadingSkeleton loading={loading} preset="display" width="60%" height={36}>
            <Typography
              variant="display"
              color="text.primary"
            >
              {value ?? '—'}
            </Typography>
          </LoadingSkeleton>
        </Box>

        {/* Footer: trend chip + sparkline OR plain subtitle */}
        {(hasSparkline || subtitle || trend != null) && (
          <Box sx={{ mt: 0.75 }}>
            <LoadingSkeleton
              loading={loading}
              preset={hasSparkline ? 'card' : 'text'}
              height={hasSparkline ? 44 : 22}
              width={hasSparkline ? '100%' : 120}
            >
              {hasSparkline ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    mt: 0.75,
                    gap: 1,
                  }}
                >
                  <Box>
                    {trend != null && (
                      <Chip
                        label={`${positive ? '+' : ''}${trend}% ${trendLabel ?? ''}`}
                        size="small"
                        sx={{
                          height: 22,
                          bgcolor: positive
                            ? 'rgba(46,158,91,0.12)'
                            : 'rgba(214,69,69,0.12)',
                          color: `${trendColor}.main`,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ width: 110, height: 44, flexShrink: 0 }}>
                    <CountingChart
                      variant="sparkline"
                      data={sparklineData}
                      valueKey="v"
                      height={44}
                      color={positive ? '#2E9E5B' : '#D64545'}
                    />
                  </Box>
                </Box>
              ) : (
                <Box>
                  {trend != null && (
                    <Chip
                      label={`${positive ? '+' : ''}${trend}% ${trendLabel ?? ''}`}
                      size="small"
                      sx={{
                        height: 22,
                        mt: 0.75,
                        bgcolor: positive
                          ? 'rgba(46,158,91,0.12)'
                          : 'rgba(214,69,69,0.12)',
                        color: `${trendColor}.main`,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                  {subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              )}
            </LoadingSkeleton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.node,
  subtitle: PropTypes.string,
  trend: PropTypes.number,
  trendLabel: PropTypes.string,
  sparklineData: PropTypes.arrayOf(PropTypes.shape({ v: PropTypes.number })),
  icon: PropTypes.node,
  accentColor: PropTypes.string,
  loading: PropTypes.bool,
};

export default StatCard;
